import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Target, Phone, MessageSquare, Mail, Check, Loader2, Clock, CalendarClock,
  Reply, LogOut, ArrowRight, CircleDashed, XCircle, SkipForward, RefreshCw,
  Pause, Play, PhoneCall, PhoneOff, Voicemail, Pencil, Send,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import api, { apiError } from "../lib/api";
import { formatDateTime } from "../lib/utils-format";

const KIND_ICON = { intro: Send, call: Phone, sms: MessageSquare, email: Mail };

const CALL_OUTCOMES = [
  { value: "Reached", icon: PhoneCall, tone: "text-emerald-400 hover:bg-emerald-500/10" },
  { value: "No Answer", icon: PhoneOff, tone: "text-muted-foreground hover:bg-secondary" },
  { value: "Voicemail", icon: Voicemail, tone: "text-primary hover:bg-primary/10" },
];

const OPEN_WI = ["pending", "claimed", "in_progress", "snoozed"];

const STATUS_TONE = {
  active: "bg-primary/15 text-primary",
  paused: "bg-amber-500/15 text-amber-400",
  completed: "bg-emerald-500/15 text-emerald-400",
  exited: "bg-secondary text-muted-foreground",
};

const STEP_TONE = {
  done: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  awaiting: "bg-primary/10 text-primary border-primary/30",
  current: "bg-primary/15 text-primary border-primary/40",
  pending: "bg-secondary/60 text-muted-foreground border-border",
  skipped: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  cancelled: "bg-destructive/10 text-destructive border-destructive/30",
};

const STEP_ICON = {
  done: Check, awaiting: PhoneCall, current: CircleDashed, pending: Clock, skipped: SkipForward, cancelled: XCircle,
};

function prettyExit(reason) {
  if (!reason) return "";
  const map = {
    lead_replied: "Lead replied",
    first_response_recorded: "First response recorded",
    do_not_contact: "Marked do-not-contact",
    opportunity_created: "Converted to opportunity",
    manual_stop: "Stopped manually",
  };
  if (map[reason]) return map[reason];
  if (reason.startsWith("status:")) return `Status → ${reason.split(":")[1]}`;
  return reason.replace(/_/g, " ");
}

function Metric({ icon: Icon, label, value, tone = "" }) {
  return (
    <div className="rounded-lg border border-border bg-card/60 p-4">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1.5">
        {Icon && <Icon className="h-3 w-3" />}{label}
      </div>
      <div className={`text-xl font-semibold ${tone}`}>{value}</div>
    </div>
  );
}

function DetailField({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1">{label}</div>
      <div className="text-sm text-foreground/90 font-medium break-words">{value ?? "—"}</div>
    </div>
  );
}

// Chevron pathway matching the lead status path. Touch steps + a final "Engaged"
// stage that lights up when the customer has responded.
export function CadencePathway({ data }) {
  const replied = !!data.replied;
  const currentStep = data.current_step;
  const segs = [
    ...data.steps.map((s) => ({ label: s.kind === "intro" ? "Intro" : s.when, status: s.status })),
    { label: "Engaged", status: "engaged_stage" },
  ];
  const point = 13;
  const clipFor = (i, last) =>
    last
      ? `polygon(0 0, 100% 0, 100% 100%, 0 100%, ${i === 0 ? 0 : point}px 50%)`
      : i === 0
      ? `polygon(0 0, calc(100% - ${point}px) 0, 100% 50%, calc(100% - ${point}px) 100%, 0 100%)`
      : `polygon(0 0, calc(100% - ${point}px) 0, 100% 50%, calc(100% - ${point}px) 100%, 0 100%, ${point}px 50%)`;

  const classFor = (seg, i, isEngaged) => {
    if (isEngaged) {
      return replied
        ? "bg-emerald-500 text-white font-semibold"
        : "bg-secondary/50 text-muted-foreground";
    }
    if (replied) return "bg-emerald-500/20 text-foreground";
    if (["done", "skipped"].includes(seg.status)) return "bg-primary/25 text-foreground";
    if (["current", "awaiting"].includes(seg.status)) return "bg-primary text-primary-foreground font-semibold";
    return "bg-secondary/50 text-muted-foreground";
  };

  return (
    <div className="rounded-lg border border-border bg-card p-1.5" data-testid="cadence-pathway">
      <div className="flex w-full">
        {segs.map((seg, i) => {
          const last = i === segs.length - 1;
          return (
            <div
              key={i}
              data-testid={`cadence-pathway-step-${last ? "engaged" : i}`}
              title={last ? "Engaged — customer responded" : `Touch ${i + 1}: ${seg.label}`}
              className={`flex-1 min-w-0 truncate text-center whitespace-nowrap text-xs py-2 ${classFor(seg, i, last)} ${i === 0 ? "pl-3 pr-4" : "pl-5 pr-4"}`}
              style={{ clipPath: clipFor(i, last), marginLeft: i ? -point : 0 }}
            >
              {seg.label}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function CadenceTrackerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stopping, setStopping] = useState(false);
  const [busy, setBusy] = useState(false);
  const [outcomeBusy, setOutcomeBusy] = useState(null);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleVal, setRescheduleVal] = useState("");
  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get(`/leads/${id}/cadence`).then((r) => r.data),
      api.get(`/leads/${id}`).then((r) => r.data).catch(() => null),
    ]).then(([c, l]) => { setData(c); setLead(l); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const stop = async () => {
    setStopping(true);
    try {
      await api.post(`/leads/${id}/cadence/stop`);
      toast.success("Cadence stopped");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setStopping(false); }
  };

  const pauseResume = async (action) => {
    setBusy(true);
    try {
      await api.post(`/leads/${id}/cadence/${action}`);
      toast.success(action === "pause" ? "Cadence paused" : "Cadence resumed");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const openReschedule = () => {
    const base = data?.next_at ? new Date(data.next_at) : new Date();
    // datetime-local wants YYYY-MM-DDTHH:mm in local time
    const off = base.getTimezoneOffset() * 60000;
    setRescheduleVal(new Date(base - off).toISOString().slice(0, 16));
    setRescheduleOpen(true);
  };

  const saveReschedule = async () => {
    if (!rescheduleVal) return;
    setBusy(true);
    try {
      await api.post(`/leads/${id}/cadence/reschedule`, { next_at: new Date(rescheduleVal).toISOString() });
      toast.success("Next touch rescheduled");
      setRescheduleOpen(false); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const logOutcome = async (step, outcome) => {
    if (!step.work_item_id) return;
    setOutcomeBusy(`${step.index}:${outcome}`);
    try {
      await api.post(`/work-items/${step.work_item_id}/complete`, { outcome });
      toast.success(`Call logged: ${outcome}`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setOutcomeBusy(null); }
  };

  const leadName = data?.lead_name || lead?.company_name || `${lead?.first_name || ""} ${lead?.last_name || ""}`.trim() || "Lead";
  const status = data?.status;
  const isActive = status === "active";
  const isPaused = status === "paused";

  return (
    <div className="p-6 lg:p-8 max-w-[1100px] mx-auto">
      <button onClick={() => navigate(`/record/analysis/${id}`)} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-5" data-testid="cadence-back-btn">
        <ArrowLeft className="h-4 w-4" />Back to lead
      </button>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-6 border-b border-border">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-primary mb-1.5">Cadence Tracker</div>
          <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-3" data-testid="cadence-page-title">
            <Target className="h-6 w-6 text-primary" />{leadName}
            {status && (
              <span className={`text-[11px] font-medium px-2.5 py-1 rounded-full capitalize ${STATUS_TONE[status] || "bg-secondary"}`} data-testid="cadence-page-status">
                {status}
              </span>
            )}
          </h1>
          <p className="text-sm text-muted-foreground mt-1.5">Multi-touch outreach schedule and progress for this lead.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} data-testid="cadence-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1.5" />Refresh
          </Button>
          {data?.current_work_item_id && (
            <Button variant="outline" onClick={() => navigate("/work-queue")} data-testid="cadence-goto-task-btn">
              Go to current task<ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          )}
          {isActive && (
            <Button variant="outline" onClick={() => pauseResume("pause")} disabled={busy} data-testid="cadence-pause-btn">
              <Pause className="h-4 w-4 mr-1.5" />Pause
            </Button>
          )}
          {isPaused && (
            <Button variant="outline" onClick={() => pauseResume("resume")} disabled={busy} data-testid="cadence-resume-btn">
              <Play className="h-4 w-4 mr-1.5" />Resume
            </Button>
          )}
          {(isActive || isPaused) && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="text-white hover:text-white" disabled={stopping} data-testid="cadence-stop-btn">
                  {stopping ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <LogOut className="h-4 w-4 mr-1.5" />}Stop cadence
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="cadence-stop-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Stop this cadence?</AlertDialogTitle>
                  <AlertDialogDescription>
                    No further automated touches or draft tasks will be created for this lead. Any pending cadence drafts in the Work Queue will be cancelled. This can't be resumed automatically.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="cadence-stop-cancel">Keep running</AlertDialogCancel>
                  <AlertDialogAction onClick={stop} data-testid="cadence-stop-confirm" className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Stop cadence</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {loading || !data ? (
        <div className="space-y-4"><Skeleton className="h-24" /><Skeleton className="h-64" /></div>
      ) : (
        <div className="space-y-8">
          {/* Summary / health */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="cadence-summary">
            <Metric icon={Target} label="Progress" value={`${data.done_count} of ${data.total_steps}`} />
            <Metric icon={CalendarClock} label="Days in cadence" value={data.days_in_cadence ?? "—"} />
            <Metric icon={Clock} label={isPaused ? "Next touch (paused)" : "Next touch"} value={(isActive || isPaused) && data.next_at ? formatDateTime(data.next_at) : "—"} />
            <Metric icon={Reply} label="Reply" value={data.replied ? "Received" : "None"} tone={data.replied ? "text-emerald-400" : ""} />
          </div>

          {/* Pathway progress */}
          <CadencePathway data={data} />

          {/* Touch breakdown */}
          <div className="grid grid-cols-3 gap-4" data-testid="cadence-breakdown">
            <Metric icon={Phone} label="Calls made" value={data.touch_breakdown.calls_made} />
            <Metric icon={MessageSquare} label="SMS sent" value={data.touch_breakdown.sms_sent} />
            <Metric icon={Mail} label="Emails sent" value={data.touch_breakdown.emails_sent} />
          </div>

          {/* Cadence details (mirrors the lead record's cadence fields) */}
          <div className="rounded-lg border border-border bg-card/60 p-5" data-testid="cadence-details">
            <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-4">Cadence details</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
              <DetailField label="Cadence Status" value={<span className="capitalize">{data.status}</span>} />
              <DetailField label="Cadence Step" value={(() => {
                const i = data.steps.findIndex((s) => s.status === "current");
                if (i >= 0) return `${i + 1} of ${data.total_steps} · ${data.steps[i].label}`;
                if (data.status === "completed") return "Completed";
                if (data.status === "exited") return "Exited";
                return `${data.done_count} of ${data.total_steps}`;
              })()} />
              <DetailField label="Cadence Started At" value={data.started_at ? formatDateTime(data.started_at) : "—"} />
              <DetailField label="Cadence Next At" value={data.next_at ? formatDateTime(data.next_at) : "—"} />
              <DetailField label="Auto Response Sent At" value={lead?.auto_response_sent_at ? formatDateTime(lead.auto_response_sent_at) : "—"} />
              <DetailField label="Last Contacted Date" value={lead?.last_contacted_date ? formatDateTime(lead.last_contacted_date) : "—"} />
              <DetailField label="Lead Replied" value={data.replied ? "Yes" : "No"} />
              {data.exit_reason && <DetailField label="Cadence Exit Reason" value={prettyExit(data.exit_reason)} />}
            </div>
          </div>

          {/* Timeline */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-3">Touch timeline</div>
            <ol className="space-y-2.5">
              {data.steps.map((s, i) => {
                const KindIcon = KIND_ICON[s.kind] || Mail;
                const SIcon = STEP_ICON[s.status] || Clock;
                return (
                  <li key={s.index}
                    onClick={() => s.work_item_id && navigate(`/work-queue/${s.work_item_id}`)}
                    className={`flex items-start gap-3 rounded-lg border p-4 ${STEP_TONE[s.status] || STEP_TONE.pending} ${s.work_item_id ? "cursor-pointer hover:border-primary/50 transition-colors" : ""}`}
                    data-testid={`cadence-step-${s.index}`}>
                    <div className="mt-0.5 h-7 w-7 rounded-full bg-background/40 flex items-center justify-center shrink-0 text-xs font-medium">
                      {s.status === "done" ? <SIcon className="h-4 w-4" /> : <span>{i + 1}</span>}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <KindIcon className="h-4 w-4 opacity-80" />
                        <span className="font-semibold text-sm text-foreground">{s.when} · {s.label}</span>
                        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-background/40 capitalize flex items-center gap-1">
                          <SIcon className="h-3 w-3" />{s.status}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-x-6 gap-y-0.5 mt-1.5 text-xs text-muted-foreground">
                        <span>Channel: <span className="text-foreground/80">{s.channel}</span></span>
                        {s.scheduled_at && <span>Scheduled: <span className="text-foreground/80">{formatDateTime(s.scheduled_at)}</span></span>}
                        {s.completed_at && <span>Completed: <span className="text-foreground/80">{formatDateTime(s.completed_at)}</span></span>}
                        {s.outcome && <span>Outcome: <span className="text-foreground/80 font-medium">{s.outcome}</span></span>}
                      </div>
                    </div>

                    {/* Actions — reschedule the current step; calls happen in the queue record */}
                    {(s.status === "current" || s.status === "awaiting") && (isActive || isPaused) && (
                      <div className="flex flex-col items-stretch gap-2 shrink-0 self-center ml-2 border-l border-border/50 pl-4">
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openReschedule(); }} data-testid={`cadence-reschedule-${s.index}`}>
                          <Pencil className="h-3.5 w-3.5 mr-1.5" />Reschedule
                        </Button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      )}

      {/* Reschedule dialog */}
      <Dialog open={rescheduleOpen} onOpenChange={setRescheduleOpen}>
        <DialogContent data-testid="cadence-reschedule-dialog">
          <DialogHeader>
            <DialogTitle>Reschedule next touch</DialogTitle>
            <DialogDescription>Pick a new date and time for the next cadence touch on this lead.</DialogDescription>
          </DialogHeader>
          <Input type="datetime-local" value={rescheduleVal} onChange={(e) => setRescheduleVal(e.target.value)} data-testid="cadence-reschedule-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRescheduleOpen(false)} data-testid="cadence-reschedule-cancel">Cancel</Button>
            <Button onClick={saveReschedule} disabled={busy || !rescheduleVal} data-testid="cadence-reschedule-save">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
